import createDOMPurify from 'dompurify'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'

/**
 * Renders an SVG asset inline when (and only when) it is one of our animated
 * marketing diagrams. Chrome freezes CSS animations inside SVGs loaded via
 * <img>, so those assets must be inlined to animate; every other SVG asset
 * (logos, icons) keeps the exact <img> rendering it has today.
 *
 * Contract with the assets (see marketing-media-tools:
 * tasks/2026-07-20-dbt-integration-page/inline-svg-pr-spec.md):
 * - animated SVGs carry class="anim-svg" on the root element
 * - they are self-contained (styles, fonts, and images inlined as data URIs)
 * - they define their own prefers-reduced-motion fallback
 *
 * SSR renders the <img> fallback; after hydration the SVG text is fetched
 * (same-origin /cf-assets proxy) and swapped in if it qualifies. Entrance
 * animations stay paused until the SVG scrolls into view (CSS rule in
 * inline-animated-svg.css + the IntersectionObserver below).
 */

const ANIMATED_MARKER = 'anim-svg'
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'

const purifier = typeof window === 'undefined' ? null : createDOMPurify(window)

/** Cache fetched decisions per URL: markup to inline, or null for "keep <img>". */
const svgCache = new Map<string, string | null>()

/**
 * Sanitizes a candidate as SVG and returns only the sanitized root element.
 * Anything that is not a single marked SVG document keeps the <img> path.
 */
export function sanitizeInlineableSvg(text: string): string | null {
  if (!purifier?.isSupported) return null

  const trimmed = text.trimStart()
  if (!trimmed.startsWith('<svg')) return null

  const fragment = purifier.sanitize(trimmed, {
    USE_PROFILES: { svg: true, svgFilters: true },
    FORBID_TAGS: ['foreignObject'],
    RETURN_DOM_FRAGMENT: true,
  })
  const root = fragment.firstElementChild

  if (
    fragment.childElementCount !== 1 ||
    root?.namespaceURI !== SVG_NAMESPACE ||
    root.localName !== 'svg' ||
    !root.classList.contains(ANIMATED_MARKER)
  ) {
    return null
  }

  return root.outerHTML
}

interface InlineAnimatedSvgProps {
  /** Same-origin (or Contentful CDN) URL of the SVG file. */
  svgUrl: string
  /** The <img> to render on the server and for non-animated SVGs. */
  fallback: React.ReactElement
  /** Mirrors the fallback image's alt text after the inline swap. */
  accessibleName: string
  className?: string
}

export function InlineAnimatedSvg({
  svgUrl,
  fallback,
  accessibleName,
  className,
}: InlineAnimatedSvgProps) {
  const [markup, setMarkup] = useState<string | null>(
    () => svgCache.get(svgUrl) ?? null,
  )
  const [isInView, setIsInView] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const [prevSvgUrl, setPrevSvgUrl] = useState(svgUrl)
  if (svgUrl !== prevSvgUrl) {
    setPrevSvgUrl(svgUrl)
    if (svgCache.has(svgUrl)) setMarkup(svgCache.get(svgUrl) ?? null)
  }

  useEffect(() => {
    if (svgCache.has(svgUrl)) return
    let cancelled = false
    fetch(svgUrl)
      .then((res) => (res.ok ? res.text() : ''))
      .then((text) => {
        const sanitized = sanitizeInlineableSvg(text)
        svgCache.set(svgUrl, sanitized)
        if (!cancelled) setMarkup(sanitized)
      })
      .catch(() => {
        svgCache.set(svgUrl, null)
        if (!cancelled) setMarkup(null)
      })
    return () => {
      cancelled = true
    }
  }, [svgUrl])

  // Start the SVG's animations only once it is actually in view.
  useEffect(() => {
    const wrapper = ref.current
    if (!wrapper) return

    setIsInView(false)
    if (typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIsInView(true)
            observer.unobserve(entry.target)
          }
        }
      },
      { threshold: 0.35 },
    )
    observer.observe(wrapper)
    return () => observer.disconnect()
  }, [markup])

  if (!markup) {
    return fallback
  }

  return (
    <div
      ref={ref}
      className={clsx('inline-animated-svg', className, {
        'in-view': isInView || typeof IntersectionObserver === 'undefined',
      })}
      role={accessibleName ? 'img' : undefined}
      aria-label={accessibleName || undefined}
      aria-hidden={accessibleName ? undefined : true}
      // The only injected value is the SVG root returned by DOMPurify in
      // sanitizeInlineableSvg; the fetched response is never injected directly.
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  )
}
